import { createStore } from "zustand/vanilla";
import { compareByOrder, err, ok } from "@pack-it-up/shared";
import { applyView, applyTodoView, applyNotesView, applyToggle, collectTags, collectOwnTags, deriveModified, invertTagRename, planTagRename, advanceStagingState, advanceCategoryStaging, computeStagingProgress, affectableRowPackingState, nextPackingState, setCategoryCompletion, setAllParticipantsCompletion, completionDelta, computeProgress, emptyCompletion, participantsOf, } from "@pack-it-up/core";
import { memoizedOnDeps } from "./memo.js";
import { errorToMessage } from "../errorToMessage.js";
// Issue #181 — apply a rename plan row by row. Deliberately not a chunked batch: going
// through the same per-row patches an edit uses keeps `modifiedSinceImport` re-derived
// against the import snapshot (tags are snapshot content, Doc 02 §7), and a note write
// is an upsert that has to carry the note's body and links along. Lists are small
// enough that the parallel writes are fine; a batched applier can replace this without
// changing the plan.
const applyTagRename = async (deps, getState, plan) => {
    const s = getState();
    const writes = [];
    for (const e of plan.entries) {
        if (e.collection === "items") {
            const cur = s.items.find((i) => i.id === e.id);
            if (cur === undefined)
                continue;
            const next = { ...cur, ...(e.tags === null ? {} : { tags: e.tags }) };
            if (e.tags === null)
                delete next.tags;
            writes.push(deps.repos.items.updateItem({
                itemId: cur.id,
                tags: e.tags,
                ...(cur.provenance ? { modifiedSinceImport: deriveModified(next) } : {}),
            }));
        }
        else if (e.collection === "todos") {
            const cur = s.todos.find((t) => t.id === e.id);
            if (cur === undefined)
                continue;
            const next = { ...cur, ...(e.tags === null ? {} : { tags: e.tags }) };
            if (e.tags === null)
                delete next.tags;
            writes.push(deps.repos.todos.updateTodo({
                todoId: cur.id,
                tags: e.tags,
                ...(cur.provenance ? { modifiedSinceImport: deriveModified(next) } : {}),
            }));
        }
        else {
            const cur = s.notes.find((n) => n.id === e.id);
            if (cur === undefined)
                continue;
            writes.push(deps.repos.notes.upsertNote({
                id: cur.id,
                listId: cur.listId,
                body: cur.body,
                links: cur.links,
                ...(e.tags !== null ? { tags: e.tags } : {}),
                authorId: cur.authorId,
            }));
        }
    }
    const results = await Promise.all(writes);
    const failed = results.find((r) => !r.ok);
    return failed !== undefined && !failed.ok ? err(failed.error) : ok(undefined);
};
const defaultView = () => ({
    sort: "manual",
    completedToBottom: false,
    filterAssignees: "all",
    filterStatus: "all",
    query: "",
    filterTags: [],
});
const defaultNotesView = () => ({ sort: "manual", filterTags: [], query: "" });
export const createTripDetailStore = (deps) => {
    let dataSubscriptions = [];
    // Tracks the actor's own MemberSettings as this store has written it, so two
    // settings writes issued back-to-back (e.g. a todos filter change immediately
    // followed by an items filter change) each fold onto the other's patch instead of
    // both reading the same not-yet-round-tripped `trip.members[...].settings` snapshot
    // and racing to clobber each other. Reset on `open` (a fresh trip/store context).
    let localMemberSettings = null;
    // T27: staging is "on" for this actor only when their own MemberSettings.stagingOptIn
    // is on — a self-service per-trip preference, no trip-wide gate.
    const effectiveStagingEnabled = (s) => {
        const member = s.trip?.members[deps.actor];
        return member?.settings.stagingOptIn ?? false;
    };
    // The derived getters below allocate, so each is memoized on the state fields it
    // reads (see memo.ts): a `useStore` selector calling one of these must get the same
    // reference back until the underlying data changes, or React re-renders forever
    // ("The result of getSnapshot should be cached to avoid an infinite loop"). Read
    // through `store` rather than the initializer's `getState` so the state literal
    // below stays a plain object; the closures only run after `store` is assigned.
    const state = () => store.getState();
    // MemberSettings writes replace the whole sub-object (Firestore dot-path update),
    // so every write here folds the patch over whatever's currently known rather than
    // sending just the changed field — otherwise a staging-toggle write would wipe out
    // a previously-saved sticky filter, and vice versa. Folds over `localMemberSettings`
    // (updated synchronously, before the write is issued) rather than `state().trip`,
    // which only catches up once the write round-trips back through the trip stream.
    const writeMemberSettings = (patch) => {
        const s = state();
        if (s.tripId === null) {
            return Promise.resolve(err({ kind: "PermissionDenied", action: "edit your settings" }));
        }
        const current = localMemberSettings ?? s.trip?.members[deps.actor]?.settings ?? { stagingOptIn: false };
        const next = { ...current, ...patch };
        localMemberSettings = next;
        return deps.repos.trips.setMemberSettings(s.tripId, deps.actor, next);
    };
    // The actor's own MemberSettings is a memo dep in its own right (issue #228): "Done
    // last" lives there, and a settings write replaces that sub-object without
    // necessarily minting a new `trip` reference.
    const myMemberSettings = () => state().trip?.members[deps.actor]?.settings;
    const visibleItemsOf = memoizedOnDeps(() => [state().trip, state().items, state().itemsView, myMemberSettings()], ([trip, items, view]) => {
        const participants = trip ? participantsOf(trip) : [];
        // "Done last" is a per-member setting (Trip Settings), not per-view — override
        // whatever the view's own field holds with the actor's own setting.
        const cfg = { ...view, completedToBottom: trip?.members[deps.actor]?.settings.completedToBottom ?? false };
        // applyView's "manual" sort expects input pre-sorted by fractional index.
        const byOrder = [...items].sort(compareByOrder);
        return applyView(byOrder, cfg, participants);
    });
    // Todos carry per-participant completion like items, but their view also sorts by due
    // date, so it's applied via `applyTodoView` rather than `applyView`.
    const visibleTodosOf = memoizedOnDeps(() => [state().trip, state().todos, state().todosView, myMemberSettings()], ([trip, todos, view]) => {
        const cfg = { ...view, completedToBottom: trip?.members[deps.actor]?.settings.completedToBottom ?? false };
        const participants = trip ? participantsOf(trip) : [];
        // Manual order baseline (fractional index), same as visibleItems.
        return applyTodoView([...todos].sort(compareByOrder), cfg, participants);
    });
    // "manual" expects input pre-sorted by fractional index, same contract as
    // visibleItemsOf/visibleTodosOf.
    const visibleNotesOf = memoizedOnDeps(() => [state().notes, state().notesView], ([notes, view]) => applyNotesView([...notes].sort(compareByOrder), view));
    const tagPaletteOf = memoizedOnDeps(() => [state().items, state().todos, state().notes], ([items, todos, notes]) => collectTags([...items, ...todos, ...notes]));
    const selectableTagPaletteOf = memoizedOnDeps(() => [state().items, state().todos, state().notes], ([items, todos, notes]) => collectOwnTags([...items, ...todos, ...notes]));
    const progressOf = memoizedOnDeps(() => [state().trip, state().items], ([trip, items]) => computeProgress(items, trip ? participantsOf(trip) : []));
    const stagingProgressOf = memoizedOnDeps(() => [state().trip, state().items], ([trip, items]) => (trip ? computeStagingProgress(items, trip) : { packed: 0, staged: 0, required: 0 }));
    const store = createStore((set, getState) => ({
        tripId: null,
        trip: null,
        role: null,
        canEdit: false,
        categories: [],
        items: [],
        todos: [],
        notes: [],
        itemsView: defaultView(),
        todosView: defaultView(),
        notesView: defaultNotesView(),
        tagRenameUndo: null,
        error: null,
        open(tripId, userId) {
            getState().close();
            localMemberSettings = null;
            set({ tripId, itemsView: defaultView(), todosView: defaultView() });
            // Seeded once, off the first trip emission that carries this actor's member
            // entry — not resynced on every later emission, so itemsView/todosView stay
            // local-optimistic state afterward (same tradeoff as the rest of this store)
            // instead of getting stomped by a stale echo of our own write.
            let viewsSeeded = false;
            dataSubscriptions = [
                deps.repos.trips.observeTrip(tripId).subscribe((trip) => {
                    const member = trip?.members[userId];
                    // A reader who isn't in members can only be here via a link grant or public
                    // visibility (Doc 08 §4/§7) — they read as a viewer, so the UI shows the
                    // read-only affordances.
                    const role = member && member.kind === "user" ? member.role : trip !== null ? "viewer" : null;
                    set({ trip, role, canEdit: role === "owner" || role === "editor" });
                    if (!viewsSeeded && member) {
                        viewsSeeded = true;
                        const { itemsFilter, todosFilter } = member.settings;
                        set({
                            itemsView: { ...getState().itemsView, ...(itemsFilter ?? {}) },
                            todosView: { ...getState().todosView, ...(todosFilter ?? {}) },
                        });
                    }
                }),
                deps.repos.categories.observeCategories(tripId).subscribe((categories) => set({ categories })),
                deps.repos.items.observeItems(tripId).subscribe((items) => set({ items })),
                deps.repos.todos.observeTodos(tripId).subscribe((todos) => set({ todos })),
                deps.repos.notes.observeNotes(tripId).subscribe((notes) => set({ notes })),
            ];
        },
        close() {
            for (const unsubscribe of dataSubscriptions)
                unsubscribe();
            dataSubscriptions = [];
            set({ tripId: null, trip: null, role: null, canEdit: false, categories: [], items: [], todos: [], notes: [] });
        },
        setItemsView(patch) {
            const itemsView = { ...getState().itemsView, ...patch };
            set({ itemsView });
            // The free-text search box is the one field left out — it changes on every
            // keystroke, and persisting it would mean a Firestore write per keystroke
            // rather than per discrete toolbar action.
            if (patch.filterStatus !== undefined ||
                patch.filterAssignees !== undefined ||
                patch.sort !== undefined ||
                patch.filterTags !== undefined) {
                void writeMemberSettings({
                    itemsFilter: {
                        filterStatus: itemsView.filterStatus,
                        filterAssignees: itemsView.filterAssignees,
                        sort: itemsView.sort,
                        filterTags: itemsView.filterTags,
                    },
                });
            }
        },
        setTodosView(patch) {
            const todosView = { ...getState().todosView, ...patch };
            set({ todosView });
            if (patch.filterStatus !== undefined ||
                patch.filterAssignees !== undefined ||
                patch.sort !== undefined ||
                patch.filterTags !== undefined) {
                void writeMemberSettings({
                    todosFilter: {
                        filterStatus: todosView.filterStatus,
                        filterAssignees: todosView.filterAssignees,
                        sort: todosView.sort,
                        filterTags: todosView.filterTags,
                    },
                });
            }
        },
        setNotesView(patch) {
            set({ notesView: { ...getState().notesView, ...patch } });
        },
        visibleItems: visibleItemsOf,
        visibleTodos: visibleTodosOf,
        visibleNotes: visibleNotesOf,
        tagPalette: tagPaletteOf,
        selectableTagPalette: selectableTagPaletteOf,
        progress: progressOf,
        stagingEnabled() {
            return effectiveStagingEnabled(getState());
        },
        stagingProgress: stagingProgressOf,
        async toggleItem(itemId, target, targetIsUser) {
            const s = getState();
            if (!s.canEdit) {
                set({ error: "You have view-only access to this trip." });
                return null;
            }
            const cur = s.items.find((i) => i.id === itemId);
            if (!cur)
                return null;
            const res = applyToggle(cur, deps.actor, target, targetIsUser, {
                allowProxyCompletion: s.trip?.settings.allowProxyCompletion ?? false,
            });
            if (!res.ok) {
                set({ error: "You can't check another person's box." });
                return null;
            }
            // optimistic, same-tick: progress() recomputes from this immediately
            set({ items: s.items.map((i) => (i.id === itemId ? res.item : i)) });
            // Persist each changed participant path (usually one; a shared uncheck can clear several).
            const writes = completionDelta(cur.completions, res.item.completions);
            const results = await Promise.all(writes.map(([pid, done]) => deps.repos.items.setCompletion(cur.id, pid, done)));
            return results.find((r) => !r.ok) ?? ok(undefined);
        },
        async advanceItemStaging(itemId, target, targetIsUser) {
            const s = getState();
            if (!s.canEdit) {
                set({ error: "You have view-only access to this trip." });
                return null;
            }
            const cur = s.items.find((i) => i.id === itemId);
            if (!cur)
                return null;
            const res = advanceStagingState(cur, deps.actor, target, targetIsUser, effectiveStagingEnabled(s), {
                allowProxyCompletion: s.trip?.settings.allowProxyCompletion ?? false,
            });
            if (!res.ok) {
                set({ error: "You can't check another person's box." });
                return null;
            }
            set({ items: s.items.map((i) => (i.id === itemId ? res.item : i)) });
            const completionWrites = completionDelta(cur.completions, res.item.completions);
            const stagingWrites = completionDelta(cur.staging ?? emptyCompletion(), res.item.staging ?? emptyCompletion());
            const results = await Promise.all([
                ...completionWrites.map(([pid, done]) => deps.repos.items.setCompletion(cur.id, pid, done)),
                ...stagingWrites.map(([pid, done]) => deps.repos.items.setStaging(cur.id, pid, done)),
            ]);
            return results.find((r) => !r.ok) ?? ok(undefined);
        },
        async advanceCategoryStaging(categoryId) {
            const s = getState();
            if (!s.canEdit || !s.trip)
                return;
            const before = s.items;
            const after = advanceCategoryStaging(before, categoryId, s.trip, deps.actor, effectiveStagingEnabled(s), {
                allowProxyCompletion: s.trip.settings.allowProxyCompletion,
            });
            set({ items: after });
            const writes = [];
            for (let i = 0; i < before.length; i++) {
                const b = before[i];
                const a = after[i];
                if (a === b)
                    continue;
                for (const [pid, done] of completionDelta(b.completions, a.completions)) {
                    writes.push(deps.repos.items.setCompletion(b.id, pid, done));
                }
                for (const [pid, done] of completionDelta(b.staging ?? emptyCompletion(), a.staging ?? emptyCompletion())) {
                    writes.push(deps.repos.items.setStaging(b.id, pid, done));
                }
            }
            await Promise.all(writes);
        },
        async setCategoryCompletion(kind, categoryId, done) {
            const s = getState();
            if (!s.canEdit || !s.trip)
                return;
            const opts = { allowProxyCompletion: s.trip.settings.allowProxyCompletion };
            if (kind === "items") {
                const before = s.items;
                const after = setCategoryCompletion(before, categoryId, s.trip, deps.actor, done, opts);
                set({ items: after });
                const writes = [];
                for (let i = 0; i < before.length; i++) {
                    const b = before[i];
                    const a = after[i];
                    if (a === b)
                        continue;
                    for (const [pid, d] of completionDelta(b.completions, a.completions)) {
                        writes.push(deps.repos.items.setCompletion(b.id, pid, d));
                    }
                    for (const [pid, d] of completionDelta(b.staging ?? emptyCompletion(), a.staging ?? emptyCompletion())) {
                        writes.push(deps.repos.items.setStaging(b.id, pid, d));
                    }
                }
                await Promise.all(writes);
                return;
            }
            const before = s.todos;
            const after = setCategoryCompletion(before, categoryId, s.trip, deps.actor, done, opts);
            set({ todos: after });
            const writes = [];
            for (let i = 0; i < before.length; i++) {
                const b = before[i];
                const a = after[i];
                if (a === b)
                    continue;
                for (const [pid, d] of completionDelta(b.completions, a.completions)) {
                    writes.push(deps.repos.todos.setCompletion(b.id, pid, d));
                }
            }
            await Promise.all(writes);
        },
        // Issue #203: expanded-mode item-level "check/uncheck all" — one row, every
        // relevant participant (the other bulk axis from setCategoryCompletion above).
        async setAllParticipants(kind, id, done) {
            const s = getState();
            if (!s.canEdit || !s.trip)
                return;
            const opts = { allowProxyCompletion: s.trip.settings.allowProxyCompletion };
            if (kind === "items") {
                const before = s.items.find((i) => i.id === id);
                if (!before)
                    return;
                const after = setAllParticipantsCompletion(before, s.trip, deps.actor, done, opts);
                if (after === before)
                    return;
                set({ items: s.items.map((i) => (i.id === id ? after : i)) });
                const writes = [];
                for (const [pid, d] of completionDelta(before.completions, after.completions)) {
                    writes.push(deps.repos.items.setCompletion(before.id, pid, d));
                }
                for (const [pid, d] of completionDelta(before.staging ?? emptyCompletion(), after.staging ?? emptyCompletion())) {
                    writes.push(deps.repos.items.setStaging(before.id, pid, d));
                }
                await Promise.all(writes);
                return;
            }
            const before = s.todos.find((t) => t.id === id);
            if (!before)
                return;
            const after = setAllParticipantsCompletion(before, s.trip, deps.actor, done, opts);
            if (after === before)
                return;
            set({ todos: s.todos.map((t) => (t.id === id ? after : t)) });
            const writes = [];
            for (const [pid, d] of completionDelta(before.completions, after.completions)) {
                writes.push(deps.repos.todos.setCompletion(before.id, pid, d));
            }
            await Promise.all(writes);
        },
        // T27 follow-up: the tri-state sibling of setAllParticipants above — instead of a
        // force-set boolean, moves EVERY relevant participant one tier forward (unstaged ->
        // staged -> packed -> unstaged), the row-level equivalent of advanceItemStaging.
        // Todos have no staging field, so `stagingOn` is always false there, which collapses
        // nextPackingState to the same plain packed<->unstaged force-cycle setAllParticipants
        // already implements — this one entry point covers both.
        // Uses affectableRowPackingState (not rowPackingState) so the "current"/"next" target
        // is scoped to who this actor can actually move: with allowProxyCompletion off, a
        // real co-traveler's entry never advances via this control, so an aggregate over
        // every relevant participant would sit stuck at "unstaged" forever.
        async advanceAllParticipantsStaging(kind, id) {
            const s = getState();
            if (!s.canEdit || !s.trip)
                return;
            const opts = { allowProxyCompletion: s.trip.settings.allowProxyCompletion };
            const stagingOn = kind === "items" && effectiveStagingEnabled(s);
            if (kind === "items") {
                const before = s.items.find((i) => i.id === id);
                if (!before)
                    return;
                const next = nextPackingState(affectableRowPackingState(before, s.trip, deps.actor, stagingOn), stagingOn);
                const after = next === "staged"
                    ? setAllParticipantsCompletion(before, s.trip, deps.actor, true, { ...opts, field: "staging" })
                    : setAllParticipantsCompletion(before, s.trip, deps.actor, next === "packed", opts);
                if (after === before)
                    return;
                set({ items: s.items.map((i) => (i.id === id ? after : i)) });
                const writes = [];
                for (const [pid, d] of completionDelta(before.completions, after.completions)) {
                    writes.push(deps.repos.items.setCompletion(before.id, pid, d));
                }
                for (const [pid, d] of completionDelta(before.staging ?? emptyCompletion(), after.staging ?? emptyCompletion())) {
                    writes.push(deps.repos.items.setStaging(before.id, pid, d));
                }
                await Promise.all(writes);
                return;
            }
            const before = s.todos.find((t) => t.id === id);
            if (!before)
                return;
            const next = nextPackingState(affectableRowPackingState(before, s.trip, deps.actor, false), false);
            const after = setAllParticipantsCompletion(before, s.trip, deps.actor, next === "packed", opts);
            if (after === before)
                return;
            set({ todos: s.todos.map((t) => (t.id === id ? after : t)) });
            const writes = [];
            for (const [pid, d] of completionDelta(before.completions, after.completions)) {
                writes.push(deps.repos.todos.setCompletion(before.id, pid, d));
            }
            await Promise.all(writes);
        },
        async renameTag(from, to) {
            const s = getState();
            if (!s.canEdit) {
                const e = { kind: "PermissionDenied", action: "rename a tag" };
                set({ error: errorToMessage(e) });
                return err(e);
            }
            const plan = planTagRename(s, from, to);
            if (plan.entries.length === 0) {
                set({ tagRenameUndo: null, error: null });
                return ok(plan);
            }
            const r = await applyTagRename(deps, getState, plan);
            if (!r.ok) {
                set({ error: errorToMessage(r.error) });
                return r;
            }
            set({ tagRenameUndo: invertTagRename(plan), error: null });
            return ok(plan);
        },
        async undoTagRename() {
            const inverse = getState().tagRenameUndo;
            if (inverse === null)
                return;
            // Cleared first: the undo is single-shot, and a failure leaves the error visible
            // rather than an Undo that would re-run.
            set({ tagRenameUndo: null });
            const r = await applyTagRename(deps, getState, inverse);
            if (!r.ok)
                set({ error: errorToMessage(r.error) });
        },
        async updateTrip(patch) {
            if (!getState().canEdit) {
                const e = { kind: "PermissionDenied", action: "edit this trip" };
                set({ error: errorToMessage(e) });
                return err(e);
            }
            const r = await deps.repos.trips.updateTrip(patch);
            if (!r.ok)
                set({ error: errorToMessage(r.error) });
            return r;
        },
        async archiveTrip() {
            const s = getState();
            if (!s.canEdit || s.tripId === null) {
                const e = { kind: "PermissionDenied", action: "archive this trip" };
                set({ error: errorToMessage(e) });
                return err(e);
            }
            const r = await deps.repos.trips.archiveTrip(s.tripId, true);
            if (!r.ok)
                set({ error: errorToMessage(r.error) });
            return r;
        },
        // T27: self-scoped — any participant (any role) may flip their OWN opt-in, never
        // gated on canEdit (a viewer can still choose to see the staged state).
        async setMyStagingOptIn(optIn) {
            const s = getState();
            if (s.tripId === null) {
                const e = { kind: "PermissionDenied", action: "edit your settings" };
                set({ error: errorToMessage(e) });
                return err(e);
            }
            const r = await writeMemberSettings({ stagingOptIn: optIn });
            if (!r.ok)
                set({ error: errorToMessage(r.error) });
            return r;
        },
        async setMyCompletedToBottom(value) {
            const r = await writeMemberSettings({ completedToBottom: value });
            if (!r.ok)
                set({ error: errorToMessage(r.error) });
            return r;
        },
        async setMyTemplateDisplay(value) {
            const r = await writeMemberSettings({ templateDisplay: value });
            if (!r.ok)
                set({ error: errorToMessage(r.error) });
            return r;
        },
        async reorderNote(noteId, order) {
            const s = getState();
            if (!s.canEdit) {
                const e = { kind: "PermissionDenied", action: "reorder notes" };
                set({ error: errorToMessage(e) });
                return err(e);
            }
            // optimistic, same-tick: the dragged card doesn't wait on the stream to settle.
            set({ notes: s.notes.map((n) => (n.id === noteId ? { ...n, order } : n)) });
            const r = await deps.repos.notes.moveNote(noteId, order);
            if (!r.ok)
                set({ error: errorToMessage(r.error) });
            return r;
        },
    }));
    return store;
};
//# sourceMappingURL=tripDetailStore.js.map